/* Ascend store locator. Progressive enhancement over the server-rendered list.
   Exposes a single global namespace: window.AscendStoreLocator. */
(function () {
  'use strict';

  if (window.AscendStoreLocator) return;

  var TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  var TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  var SELECT_ZOOM = 14;
  var SINGLE_RESULT_ZOOM = 13;
  // Popup padding inside the map, in px: [x, y]. Top-left clears the zoom buttons.
  var POPUP_PAD_TOP_LEFT = [70, 12];
  var POPUP_PAD_BOTTOM_RIGHT = [12, 12];

  var PIN_SVG =
    '<svg class="sl-pin__svg" viewBox="0 0 32 44" width="32" height="44" aria-hidden="true" focusable="false">' +
    '<path class="sl-pin__body" d="M16 1.5C8 1.5 1.5 8 1.5 16c0 10.6 12.3 24.4 13.4 25.6a1.5 1.5 0 0 0 2.2 0C18.2 40.4 30.5 26.6 30.5 16 30.5 8 24 1.5 16 1.5z"/>' +
    '<circle class="sl-pin__dot" cx="16" cy="16" r="5.5"/>' +
    '</svg>';

  var reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reducedMotion() {
    return !!(reducedMotionQuery && reducedMotionQuery.matches);
  }

  // Matches the store-locator.css breakpoint where list and map sit side by side.
  var sideBySideQuery = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;
  function isSideBySide() {
    return !!(sideBySideQuery && sideBySideQuery.matches);
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\b(\d{5})-\d{4}\b/g, '$1') // ZIP+4 -> ZIP
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Query words that carry no filtering value (every store is in the US).
  var IGNORED_TOKENS = { usa: true, us: true, united: true, states: true, america: true };
  var TOKEN_ALIASES = { california: 'ca' };

  function queryTokens(q) {
    if (!q) return [];
    return q
      .split(' ')
      .map(function (t) {
        return TOKEN_ALIASES[t] || t;
      })
      .filter(function (t) {
        return t && !IGNORED_TOKENS[t];
      });
  }

  function formatMiles(d) {
    if (d < 10) return d.toFixed(1);
    var n = Math.round(d);
    try {
      return n.toLocaleString('en-US');
    } catch (e) {
      return String(n);
    }
  }

  // Beyond this, the user's position is not added to the map bounds (the pins would be off-screen).
  var NEARBY_RADIUS_MI = 150;

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function milesBetween(lat1, lng1, lat2, lng2) {
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLng = (lng2 - lng1) * toRad;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function pluralStores(n) {
    return n === 1 ? '1 store' : n + ' stores';
  }

  function hasCoords(store) {
    return typeof store.lat === 'number' && typeof store.lng === 'number' && isFinite(store.lat) && isFinite(store.lng);
  }

  function StoreLocator(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.stores = [];
    this.byId = {};
    this.cards = [];
    this.map = null;
    this.markerLayer = null;
    this.selectedId = null;
    this.query = '';
    this.userPosition = null;
    this.observer = null;
    this.userMarker = null;
    this.viewSet = false;
    this.openingFromList = false;
    this.destroyed = false;

    var dataEl = document.querySelector('script[data-sl-data="' + this.sectionId + '"]');
    try {
      this.stores = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch (e) {
      this.stores = [];
    }

    var self = this;
    this.stores.forEach(function (store) {
      store.search = ' ' + normalize([store.name, store.street, store.city, store.state, store.zip].join(' ')) + ' ';
      store.card = root.querySelector('[data-store-id="' + store.id + '"]');
      store.marker = null;
      store.distance = null;
      self.byId[store.id] = store;
    });

    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-sl-card]'));
    // Remember each card's original place so the region view can be restored.
    this.cardHomes = this.cards.map(function (card) {
      return { card: card, parent: card.parentNode };
    });

    this.els = {
      count: root.querySelector('[data-sl-count]'),
      search: root.querySelector('[data-sl-search]'),
      form: root.querySelector('[data-sl-search-form]'),
      locate: root.querySelector('[data-sl-locate]'),
      locateLabel: root.querySelector('[data-sl-locate-label]'),
      status: root.querySelector('[data-sl-status]'),
      empty: root.querySelector('[data-sl-empty]'),
      emptyMsg: root.querySelector('[data-sl-empty-message]'),
      reset: root.querySelector('[data-sl-reset]'),
      nearby: root.querySelector('[data-sl-nearby]'),
      nearbyList: root.querySelector('[data-sl-nearby-list]'),
      regions: Array.prototype.slice.call(root.querySelectorAll('[data-sl-region]')),
      mapEl: root.querySelector('[data-sl-map]'),
    };

    this.bind();
    this.setupMapLoading();
  }

  StoreLocator.prototype.bind = function () {
    var self = this;
    var els = this.els;

    if (els.form) {
      els.form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (els.search) self.query = normalize(els.search.value);
        self.applyFilter();
      });
    }

    if (els.search) {
      this.onSearchInput = debounce(function () {
        self.query = normalize(els.search.value);
        self.applyFilter();
      }, 150);
      els.search.addEventListener('input', this.onSearchInput);
      els.search.addEventListener('search', this.onSearchInput);
    }

    if (els.reset) {
      els.reset.addEventListener('click', function () {
        if (els.search) els.search.value = '';
        self.query = '';
        self.applyFilter();
        if (els.search) els.search.focus();
      });
    }

    if (els.locate) {
      if (!('geolocation' in navigator)) {
        els.locate.hidden = true;
      } else {
        els.locate.addEventListener('click', function () {
          if (self.userPosition) {
            self.clearLocation();
          } else {
            self.locate();
          }
        });
      }
    }

    this.cards.forEach(function (card) {
      var showBtn = card.querySelector('[data-sl-show]');
      var store = self.byId[card.getAttribute('data-store-id')];
      if (showBtn) {
        if (store && hasCoords(store) && els.mapEl) {
          showBtn.hidden = false;
          showBtn.addEventListener('click', function () {
            self.selectStore(store.id, { fromList: true });
          });
        } else {
          showBtn.hidden = true;
        }
      }
      if (store && hasCoords(store) && els.mapEl) {
        card.classList.add('sl-card--interactive');
        card.addEventListener('click', function (event) {
          if (event.target.closest('a, button, input')) return;
          self.selectStore(store.id, { fromList: true, keepFocus: true });
        });
      }
    });
  };

  /* ---------- Map ---------- */

  StoreLocator.prototype.setupMapLoading = function () {
    var self = this;
    var mapEl = this.els.mapEl;
    if (!mapEl) return;
    if (typeof window.L === 'undefined') {
      mapEl.setAttribute('data-sl-map-unavailable', '');
      return;
    }
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              self.initMap();
            }
          });
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(mapEl);
    } else {
      this.initMap();
    }
  };

  StoreLocator.prototype.initMap = function () {
    if (this.map || this.destroyed || !this.els.mapEl || typeof window.L === 'undefined') return;
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    var L = window.L;
    var self = this;
    var still = reducedMotion();

    this.map = L.map(this.els.mapEl, {
      scrollWheelZoom: false,
      zoomAnimation: !still,
      fadeAnimation: !still,
      markerZoomAnimation: !still,
      minZoom: 5,
      maxZoom: 19,
      zoomSnap: 0.5,
    });

    L.tileLayer(TILE_URL, {
      maxZoom: 19,
      attribution: TILE_ATTRIBUTION,
    }).addTo(this.map);

    this.markerLayer = L.featureGroup().addTo(this.map);
    var pinIcon = L.divIcon({
      className: 'sl-pin',
      html: PIN_SVG,
      iconSize: [32, 44],
      iconAnchor: [16, 44],
      popupAnchor: [0, -40],
    });

    this.stores.forEach(function (store) {
      if (!hasCoords(store)) return;
      var marker = L.marker([store.lat, store.lng], {
        icon: pinIcon,
        keyboard: true,
        title: store.name,
        riseOnHover: true,
      });
      marker.bindPopup(self.popupContent(store), {
        autoPan: !still,
        // Keep the popup clear of the top-left zoom buttons (44px tap targets).
        autoPanPaddingTopLeft: POPUP_PAD_TOP_LEFT,
        autoPanPaddingBottomRight: POPUP_PAD_BOTTOM_RIGHT,
        maxWidth: 260,
        closeButton: true,
      });
      // Pin click or Enter opens the popup (Leaflet's bindPopup); select the store when it opens.
      marker.on('popupopen', function () {
        if (self.openingFromList) return;
        self.selectStore(store.id, { fromMap: true });
      });
      marker.on('popupclose', function (e) {
        if (self.selectedId === store.id) self.highlight(null);
        // If focus was inside the popup (or fell to <body>), return it to the pin.
        var popupEl = e && e.popup && e.popup.getElement ? e.popup.getElement() : null;
        var active = document.activeElement;
        if (!active || active === document.body || (popupEl && popupEl.contains(active))) {
          var pinEl = marker.getElement();
          if (pinEl) pinEl.focus({ preventScroll: true });
        }
      });
      store.marker = marker;
    });

    this.syncMarkers(true);
  };

  StoreLocator.prototype.markPin = function (store, selected) {
    if (!store || !store.marker) return;
    var el = store.marker.getElement();
    if (el) el.classList.toggle('sl-pin--selected', !!selected);
    store.marker.setZIndexOffset(selected ? 1000 : 0);
  };

  StoreLocator.prototype.popupContent = function (store) {
    var wrap = document.createElement('div');
    wrap.className = 'sl-popup';

    var name = document.createElement('p');
    name.className = 'sl-popup__name';
    name.textContent = store.name;

    var address = document.createElement('p');
    address.className = 'sl-popup__address';
    address.appendChild(document.createTextNode(store.street));
    address.appendChild(document.createElement('br'));
    address.appendChild(document.createTextNode(store.cityLine));

    var link = document.createElement('a');
    link.className = 'sl-popup__link';
    link.href = store.directions;
    link.target = '_blank';
    link.rel = 'noopener';
    link.appendChild(document.createTextNode('Get directions'));
    var sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = ' to ' + store.name + ' (opens Google Maps in a new tab)';
    link.appendChild(sr);

    wrap.appendChild(name);
    wrap.appendChild(address);
    wrap.appendChild(link);
    return wrap;
  };

  StoreLocator.prototype.visibleStores = function () {
    return this.stores.filter(function (store) {
      return store.visible !== false;
    });
  };

  StoreLocator.prototype.syncMarkers = function (fit) {
    if (!this.map) return;
    var self = this;
    var layer = this.markerLayer;
    var points = [];
    this.stores.forEach(function (store) {
      if (!store.marker) return;
      if (store.visible === false) {
        if (layer.hasLayer(store.marker)) layer.removeLayer(store.marker);
      } else {
        if (!layer.hasLayer(store.marker)) {
          layer.addLayer(store.marker);
          self.markPin(store, store.id === self.selectedId);
        }
        points.push([store.lat, store.lng]);
      }
    });
    this.updateUserMarker();
    if (fit) this.fitTo(points);
  };

  // True when the nearest visible store is within NEARBY_RADIUS_MI of the user.
  StoreLocator.prototype.userIsNearby = function () {
    if (!this.userPosition) return false;
    var nearest = null;
    this.stores.forEach(function (store) {
      if (store.visible === false || typeof store.distance !== 'number') return;
      if (nearest === null || store.distance < nearest) nearest = store.distance;
    });
    return nearest !== null && nearest <= NEARBY_RADIUS_MI;
  };

  StoreLocator.prototype.updateUserMarker = function () {
    if (!this.map) return;
    var L = window.L;
    var show = this.userPosition && this.userIsNearby();
    if (!show) {
      if (this.userMarker) {
        this.map.removeLayer(this.userMarker);
        this.userMarker = null;
      }
      return;
    }
    var latlng = [this.userPosition.lat, this.userPosition.lng];
    if (this.userMarker) {
      this.userMarker.setLatLng(latlng);
      return;
    }
    this.userMarker = L.circleMarker(latlng, {
      radius: 7,
      color: '#fff',
      weight: 3,
      fillColor: '#000',
      fillOpacity: 1,
      interactive: true,
      bubblingMouseEvents: false,
    })
      .bindTooltip('You are here', { direction: 'top', offset: [0, -8] })
      .addTo(this.map);
  };

  StoreLocator.prototype.fitTo = function (points) {
    if (!this.map) return;
    var L = window.L;
    var animate = !reducedMotion();
    if (this.userPosition && points.length && this.userIsNearby()) {
      points = points.concat([[this.userPosition.lat, this.userPosition.lng]]);
    }
    var first = !this.viewSet;
    this.viewSet = true;
    if (points.length === 0) {
      if (first) this.map.setView([36.7, -119.4], 6, { animate: false });
      return;
    }
    if (points.length === 1) {
      this.map.setView(points[0], SINGLE_RESULT_ZOOM, { animate: animate && !first });
      return;
    }
    this.map.fitBounds(L.latLngBounds(points), {
      padding: [32, 32],
      maxZoom: SINGLE_RESULT_ZOOM,
      animate: animate && !first,
    });
  };

  /* ---------- Selection ---------- */

  StoreLocator.prototype.highlight = function (id) {
    var previous = this.selectedId ? this.byId[this.selectedId] : null;
    if (previous) {
      if (previous.card) previous.card.classList.remove('is-selected');
      this.markPin(previous, false);
    }
    this.selectedId = id;
    var store = id ? this.byId[id] : null;
    if (!store) return null;
    if (store.card) store.card.classList.add('is-selected');
    this.markPin(store, true);
    return store;
  };

  StoreLocator.prototype.selectStore = function (id, options) {
    options = options || {};
    var store = this.byId[id];
    if (!store) return;

    if (!this.map) this.initMap();
    if (!this.map || !store.marker) {
      this.highlight(id);
      return;
    }

    // Selecting a store hidden by the current search clears the search first.
    if (store.visible === false) {
      if (this.els.search) this.els.search.value = '';
      this.query = '';
      this.applyFilter(true);
    }

    var still = reducedMotion();
    this.highlight(id);

    if (options.fromList) {
      var targetZoom = Math.max(this.map.getZoom() || 0, SELECT_ZOOM);
      if (still) {
        this.map.setView([store.lat, store.lng], targetZoom, { animate: false });
      } else {
        this.map.flyTo([store.lat, store.lng], targetZoom, { duration: 0.6 });
      }
      this.scrollMapIntoView();
      this.openingFromList = true;
      try {
        store.marker.openPopup();
      } finally {
        this.openingFromList = false;
      }
      if (!options.keepFocus) {
        var el = store.marker.getElement();
        if (el) {
          try {
            el.focus({ preventScroll: true });
          } catch (e) {
            el.focus();
          }
        }
      }
      if (still) {
        this.keepPopupInView(store, false);
      } else {
        // The popup opens while flyTo is still moving, so its auto-pan is undone
        // when the flight ends centred on the pin (on the 320px mobile map that
        // hides the store name). Re-check once the flight has finished: flyTo
        // always ends with zoomend, whereas the popup's own auto-pan also fires
        // moveend mid-flight, so moveend would run this too early.
        var self = this;
        this.map.once('zoomend', function () {
          self.keepPopupInView(store, true);
        });
      }
    } else if (options.fromMap) {
      // Popups auto-pan (clearing the zoom buttons) unless reduced motion is on;
      // then shift the map once, without animation, so the popup is fully visible.
      if (still) this.keepPopupInView(store, false);
      // Only scroll the card into view when list and map sit side by side
      // (>= 990px, sticky map). Below that the map is above the list, and
      // scrolling would push the map and the just-opened popup off screen.
      if (store.card && isSideBySide()) {
        store.card.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'nearest' });
      }
    }
  };

  // Pans the map so an open popup sits fully inside it, leaving room for the zoom
  // buttons at top-left. animate is false under reduced motion (autoPan is off then).
  StoreLocator.prototype.keepPopupInView = function (store, animate) {
    var popup = store.marker && store.marker.getPopup();
    var el = popup && popup.isOpen() ? popup.getElement() : null;
    if (!el || !this.map) return;
    var m = this.map.getContainer().getBoundingClientRect();
    var p = el.getBoundingClientRect();
    var dx = 0;
    var dy = 0;
    if (p.left - m.left < POPUP_PAD_TOP_LEFT[0]) {
      dx = p.left - m.left - POPUP_PAD_TOP_LEFT[0];
    } else if (m.right - p.right < POPUP_PAD_BOTTOM_RIGHT[0]) {
      dx = POPUP_PAD_BOTTOM_RIGHT[0] - (m.right - p.right);
    }
    if (p.top - m.top < POPUP_PAD_TOP_LEFT[1]) {
      dy = p.top - m.top - POPUP_PAD_TOP_LEFT[1];
    } else if (m.bottom - p.bottom < POPUP_PAD_BOTTOM_RIGHT[1]) {
      dy = POPUP_PAD_BOTTOM_RIGHT[1] - (m.bottom - p.bottom);
    }
    if (dx || dy) {
      this.map.panBy([Math.round(dx), Math.round(dy)], { animate: !!animate && !reducedMotion() });
    }
  };

  StoreLocator.prototype.scrollMapIntoView = function () {
    var mapEl = this.els.mapEl;
    if (!mapEl) return;
    var rect = mapEl.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < 0 || rect.bottom > vh) {
      mapEl.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
    }
  };

  /* ---------- Filtering ---------- */

  StoreLocator.prototype.applyFilter = function (skipFit) {
    var q = this.query;
    var tokens = queryTokens(q);
    var shown = 0;

    this.stores.forEach(function (store) {
      var match = tokens.every(function (t) {
        return store.search.indexOf(t) !== -1;
      });
      store.visible = match;
      if (store.card) store.card.hidden = !match;
      if (match) shown += 1;
    });

    this.els.regions.forEach(function (region) {
      var any = region.querySelector('[data-sl-card]:not([hidden])');
      region.hidden = !any;
    });
    if (this.els.nearby && this.userPosition) {
      this.els.nearby.hidden = shown === 0;
    }

    if (this.els.empty) this.els.empty.hidden = shown !== 0;
    if (this.els.emptyMsg && shown === 0) {
      var zipOnly = tokens.length === 1 && /^\d{5}$/.test(tokens[0]);
      this.els.emptyMsg.textContent = zipOnly
        ? 'No store in ZIP ' + tokens[0] + '. Try “Use my location” or search by city to find the nearest one.'
        : 'No stores match your search. Try a store name, city or ZIP.';
    }

    if (this.els.count) {
      var total = this.stores.length;
      this.els.count.textContent = q ? 'Showing ' + shown + ' of ' + pluralStores(total) : pluralStores(total);
    }

    if (this.selectedId && this.byId[this.selectedId] && this.byId[this.selectedId].visible === false) {
      if (this.map) this.map.closePopup();
      this.highlight(null);
    }

    this.syncMarkers(!skipFit);
  };

  /* ---------- Geolocation ---------- */

  StoreLocator.prototype.setStatus = function (message) {
    var status = this.els.status;
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
  };

  StoreLocator.prototype.locate = function () {
    var self = this;
    var btn = this.els.locate;
    if (this.locating) return;
    this.locating = true;
    if (btn) {
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
    }
    this.setStatus('Finding your location…');

    navigator.geolocation.getCurrentPosition(
      function (position) {
        self.locating = false;
        if (btn) {
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('aria-busy');
        }
        self.userPosition = { lat: position.coords.latitude, lng: position.coords.longitude };
        self.sortByDistance();
      },
      function (error) {
        self.locating = false;
        if (btn) {
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('aria-busy');
        }
        var message =
          error && error.code === 1
            ? 'Location access was not allowed. You can still search by store name, city or ZIP.'
            : "We couldn't get your location. You can still search by store name, city or ZIP.";
        self.setStatus(message);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  StoreLocator.prototype.sortByDistance = function () {
    var self = this;
    var pos = this.userPosition;
    if (!pos || !this.els.nearbyList) return;

    var located = [];
    var unlocated = [];
    this.stores.forEach(function (store) {
      if (hasCoords(store)) {
        store.distance = milesBetween(pos.lat, pos.lng, store.lat, store.lng);
        located.push(store);
      } else {
        store.distance = null;
        unlocated.push(store);
      }
    });
    located.sort(function (a, b) {
      return a.distance - b.distance;
    });

    located.concat(unlocated).forEach(function (store) {
      if (!store.card) return;
      var distEl = store.card.querySelector('[data-sl-distance]');
      if (distEl) {
        if (store.distance !== null) {
          distEl.textContent = formatMiles(store.distance) + ' mi away';
          distEl.hidden = false;
        } else {
          distEl.hidden = true;
        }
      }
      self.els.nearbyList.appendChild(store.card);
    });

    this.els.nearby.hidden = false;
    this.root.classList.add('sl--by-distance');

    if (this.els.locateLabel) this.els.locateLabel.textContent = 'Show by region';

    var nearest = located[0];
    var message = 'Sorted by distance from your location.';
    if (nearest) {
      message +=
        nearest.distance <= NEARBY_RADIUS_MI
          ? ' Nearest: ' + nearest.name + ', ' + formatMiles(nearest.distance) + ' mi.'
          : ' The nearest store, ' + nearest.name + ', is ' + formatMiles(nearest.distance) + ' mi away.';
    }
    this.setStatus(message);

    this.applyFilter();
  };

  StoreLocator.prototype.clearLocation = function () {
    this.userPosition = null;
    this.cardHomes.forEach(function (home) {
      home.parent.appendChild(home.card);
    });
    this.cards.forEach(function (card) {
      var distEl = card.querySelector('[data-sl-distance]');
      if (distEl) {
        distEl.hidden = true;
        distEl.textContent = '';
      }
    });
    if (this.els.nearby) this.els.nearby.hidden = true;
    this.root.classList.remove('sl--by-distance');
    if (this.els.locateLabel) this.els.locateLabel.textContent = 'Use my location';
    this.setStatus('');
    this.applyFilter();
  };

  StoreLocator.prototype.destroy = function () {
    this.destroyed = true;
    if (this.observer) this.observer.disconnect();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  };

  /* ---------- Boot ---------- */

  var instances = {};

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-store-locator]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-sl-ready') === 'true') return;
      root.setAttribute('data-sl-ready', 'true');
      var id = root.getAttribute('data-section-id');
      try {
        instances[id] = new StoreLocator(root);
      } catch (e) {
        root.removeAttribute('data-sl-ready');
      }
    });
  }

  window.AscendStoreLocator = { init: initAll, instances: instances };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor support.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var id = event.detail && event.detail.sectionId;
    if (id && instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }
  });
  document.addEventListener('shopify:block:select', function (event) {
    var id = event.detail && event.detail.sectionId;
    var blockId = event.detail && event.detail.blockId;
    if (id && blockId && instances[id]) instances[id].highlight(blockId);
  });
})();
